import { useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Plus, User, Building2, LayoutGrid, UsersRound, Share2, ArrowUpDown, Filter, X, ChevronDown, ChevronRight, ClipboardPaste } from 'lucide-react'
import { isMiniappFolder, stripPointerSuffix } from '@papan/types'
import { useTranslation } from '@papan/i18n'
import { usePageTitle } from '@papan/brand/react'
import { ProjectCard, type ProjectCardVariant, type ProjectDateMode } from './ProjectCard'
import { useProjectCardActions, type FolderPicker } from './useProjectCardActions'
import {
  useProjects,
  useIncomingSharedProjects,
  sortProjects, classifyProjectSection, collectFacets, projectMatchesFacet,
  type ProjectItem, type ProjectSort, type ProjectSection, type Facet, type FacetType,
} from './data'
import { useNewProject } from '../NewProjectProvider'
import { usePasteProjects } from './usePasteProjects'
import { PDAppIcon, useSwapScroll, useMyCapabilities } from '@papan/core-ui/ui'
import { getActiveTenantId } from '@papan/api-client'
import { LinkAppSection } from '../apps/LinkAppSection'

const SECTION_ORDER: ProjectSection[] = ['mySpace', 'myTeams', 'org', 'outsider']

/**
 * The Projects & MiniApps page — one component, two variants:
 *   - `project`  — projects only; `.miniapp` folders are excluded.
 *   - `miniapp`  — only `.miniapp` folders, rendered as apps with app shortcuts.
 *
 * The two are a PARTITION — every row belongs to exactly one of them.
 *
 * Adds a sorter (name / modified / created), a facet filter (team / role /
 * member / creator), collapsible org sections (My Space · My Teams ·
 * Organization · Outsider) and a "Show as admin" visibility toggle backed by
 * the server's `hiddenWithoutAdminView` decision. See
 * docs/plans/2026-07-14_(completed)_PROJECTS_MINIAPPS_VIEW_REVAMP.md.
 */
export function ProjectsView({ urlPrefix, isOrg, orgEmail, variant = 'project', onRequestMiniappView, FolderPickerDialog, deckActive = true, tenantName }: {
  urlPrefix: string
  isOrg?: boolean
  /** This drive's tenant name — the cross-tenant paste confirmation names it (#752). */
  tenantName?: string
  orgEmail?: string
  variant?: ProjectCardVariant
  /** Is the drive deck on screen? It stays mounted when hidden. */
  deckActive?: boolean
  /** Threaded from App.tsx (owns @papan/collab): select the editor face before opening. */
  onRequestMiniappView?: (folderId: string, view: 'run' | 'settings') => void
  /** Destination folder picker for Move/Copy — injected to avoid a files → workspace edge. */
  FolderPickerDialog?: FolderPicker
}) {
  const { t } = useTranslation('files')
  const navigate = useNavigate()
  const newProject = useNewProject()
  const projects = useProjects()
  const incoming = useIncomingSharedProjects()
  const isMini = variant === 'miniapp'
  // The two variants are two surfaces sharing one component, so the tab has to
  // say which. Reuses the drive home's own nav labels — the tab and the page
  // it names cannot then drift, and both are translated. Declared AFTER
  // `isMini`: read above it this is a temporal-dead-zone crash, not a lint nit.
  usePageTitle(t(isMini ? 'driveHome.nav.miniapps' : 'driveHome.nav.projects'), deckActive)

  const [filter, setFilter] = useState('')
  const [sort, setSort] = useState<ProjectSort>('modified')
  const [dateMode, setDateMode] = useState<ProjectDateMode>('modified')
  const [facet, setFacet] = useState<Facet | null>(null)
  const [showAsAdmin, setShowAsAdmin] = useState(false)
  const [collapsed, setCollapsed] = useState<Partial<Record<ProjectSection, boolean>>>({})
  const [sortOpen, setSortOpen] = useState(false)
  const [filterOpen, setFilterOpen] = useState(false)

  // Context-menu actions + their dialogs — shared with the Home strip.
  const { handlers: cardActions, dialogs } = useProjectCardActions({ isOrg, FolderPickerDialog })

  // Drive-clipboard paste (Ctrl+V / the Paste button): a copied `.project`/
  // `.miniapp` folder lands in the caller's projects container as a new card.
  const clipboard = usePasteProjects(variant, { isOrg, tenantName, deckActive })

  // Sort → the card date subtext follows a DATE sort; a name sort keeps the last.
  const applySort = (s: ProjectSort) => {
    setSort(s)
    if (s === 'modified' || s === 'created') setDateMode(s)
    setSortOpen(false)
  }

  // 1) variant scope, 2) admin-visibility, 3) name search, 4) facet.
  //
  // The two variants PARTITION the list: a `.miniapp` folder belongs to the
  // MiniApps page and nowhere else. It used to fall through to Projects as
  // well (rendered as a plain project), which put every app in two places at
  // once and made the Projects count disagree with what Projects is for.
  const scoped = useMemo(() => {
    // Inbound cross-tenant work is appended from its own query — it lives on a
    // different endpoint because a `.shared` mount is a pointer row that the
    // workspaces walk cannot reach. Merged HERE rather than in `useProjects`
    // so the two stay separately cacheable and a failure of one does not blank
    // the other.
    const all = [...(projects.data ?? []), ...(incoming.data ?? [])]
    // The two variants PARTITION the list, and a pointer is classified by what
    // it points AT — an inbound `.pdapp.shared` is an app, and belongs on the
    // PDApps page rather than showing up under Projects as a plain row.
    return all.filter(p => isMiniappFolder(stripPointerSuffix(p.name)) === isMini)
  }, [projects.data, incoming.data, isMini])

  const visible = useMemo(() => {
    let items = scoped
    if (isOrg && !showAsAdmin) items = items.filter(p => !p.hiddenWithoutAdminView)
    const q = filter.trim().toLowerCase()
    if (q) items = items.filter(p => p.name.toLowerCase().includes(q))
    if (facet) items = items.filter(p => projectMatchesFacet(p, facet))
    return sortProjects(items, sort)
  }, [scoped, isOrg, showAsAdmin, filter, facet, sort])

  const facets = useMemo(() => collectFacets(scoped, (f) => t(
    f === 'viewer' ? 'driveView.access.viewerPlus' : f === 'member' ? 'driveView.access.memberPlus' : 'driveView.access.limited',
  )), [scoped, t])
  // Offer the "Show as admin" toggle to org admins/owners regardless of whether
  // anything is currently hidden (so the control is discoverable); it just no-ops
  // when there's no admin-noise to reveal.
  // #1102 item 3 — "Show as admin" is offered to who holds tenant-wide `manage_folder_permission:write` (never the role;
  // defaults Owner and Admin) — the same answer the server's admin view asks
  const capsTenantId = isOrg ? (getActiveTenantId() ?? undefined) : undefined
  const { can } = useMyCapabilities(capsTenantId, !isOrg, orgEmail)
  const showAdminToggle = !!isOrg && can('manage_folder_permission', 'write')

  const sections = useMemo(() => {
    const map: Record<ProjectSection, ProjectItem[]> = { mySpace: [], myTeams: [], org: [], outsider: [] }
    for (const p of visible) map[classifyProjectSection(p, orgEmail)].push(p)
    return map
  }, [visible, orgEmail])

  // Navigation is view-specific; the shared hook owns the menu/dialog actions.
  const openProject = (p: ProjectItem) => navigate(`${urlPrefix}/${isMini ? 'app' : 'workspace'}/${p.folderId}`)
  const browseProject = (p: ProjectItem) => navigate(`${urlPrefix}/folder/${p.folderId}`)
  const editSettings = (p: ProjectItem) => { onRequestMiniappView?.(p.folderId, 'settings'); navigate(`${urlPrefix}/workspace/${p.folderId}`) }
  const handlers = {
    onOpen: openProject,
    onBrowse: browseProject,
    // D118: an app's broken databases.json opens as a file
    onOpenFile: (fileId: string) => navigate(`${urlPrefix}/file/${fileId}`),
    ...cardActions,
    ...(isMini ? { onEditSettings: editSettings } : {}),
  }

  const grid = (items: ProjectItem[]) => (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-3">
      {items.map(p => (
        <ProjectCard key={p.folderId} project={p} variant={variant} dateMode={dateMode} {...handlers} />
      ))}
    </div>
  )

  const SORT_LABEL: Record<ProjectSort, string> = {
    modified: t('driveHome.projectsPage.sortModified', { defaultValue: 'Last modified' }),
    created: t('driveHome.projectsPage.sortCreated', { defaultValue: 'Date created' }),
    name: t('driveHome.projectsPage.sortName', { defaultValue: 'Name' }),
  }
  const SECTION_META: Record<ProjectSection, { icon: React.ReactNode; label: string }> = {
    mySpace: { icon: <User size={14} />, label: t('driveHome.projectsPage.sectionMySpace', { defaultValue: 'From My Space' }) },
    myTeams: { icon: <UsersRound size={14} />, label: t('driveHome.projectsPage.sectionMyTeams', { defaultValue: 'From My Teams' }) },
    org: { icon: <Building2 size={14} />, label: t('driveHome.projectsPage.sectionOrg', { defaultValue: 'From Organization' }) },
    outsider: { icon: <Share2 size={14} />, label: t('driveHome.projectsPage.sectionOutsider', { defaultValue: 'From Outsider' }) },
  }

  const FACET_GROUPS: Array<{ type: FacetType; label: string }> = [
    { type: 'team', label: t('driveHome.projectsPage.facetTeam', { defaultValue: 'Team' }) },
    { type: 'role', label: t('driveHome.projectsPage.facetRole', { defaultValue: 'Role' }) },
    { type: 'member', label: t('driveHome.projectsPage.facetMember', { defaultValue: 'Member' }) },
    { type: 'creator', label: t('driveHome.projectsPage.facetCreator', { defaultValue: 'Creator' }) },
  ]

  // #897 — keeps its place across a phone/desktop layout swap.
  const scrollRef = useRef<HTMLDivElement>(null)
  useSwapScroll(`projects:${variant}`, scrollRef)

  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto">
      <div className="max-w-5xl mx-auto w-full px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-2">
            {isMini ? <PDAppIcon size={18} className="text-fuchsia-400" /> : <LayoutGrid size={18} className="text-indigo-400" />}
            <h1 className="text-xl font-semibold themed-text">{t(isMini ? 'driveHome.nav.miniapps' : 'driveHome.nav.projects')}</h1>
            {!projects.isLoading && <span className="text-xs themed-text-muted mt-0.5">{visible.length}</span>}
          </div>
          <div className="flex items-center gap-2">
            {clipboard.pastable > 0 && (
              <button
                onClick={() => void clipboard.paste()}
                disabled={clipboard.pasting}
                className="themed-btn-secondary rounded-lg px-3.5 py-2 text-sm flex items-center gap-1.5 disabled:opacity-60"
                data-testid="projects-paste"
              >
                <ClipboardPaste size={15} />
                {t('driveView.menu.paste')}{clipboard.pastable > 1 ? ` (${clipboard.pastable})` : ''}
              </button>
            )}
            {isMini ? (
              <button
                onClick={() => newProject.open({ entityKind: 'miniapp', onDone: (fid) => navigate(`${urlPrefix}/workspace/${fid}`) })}
                className="themed-btn-primary rounded-lg px-3.5 py-2 text-sm flex items-center gap-1.5"
                data-testid="projects-new-miniapp"
              >
                <Plus size={15} />
                {t('driveView.actions.newMiniapp', 'New PDApp')}
              </button>
            ) : (
              <button
                onClick={() => newProject.open({ onDone: (fid) => navigate(`${urlPrefix}/workspace/${fid}`) })}
                className="themed-btn-primary rounded-lg px-3.5 py-2 text-sm flex items-center gap-1.5"
                data-testid="projects-new"
              >
                <Plus size={15} />
                {t('driveHome.projectsPage.new')}
              </button>
            )}
          </div>
        </div>

        {/* Controls: search · sort · filter · show-as-admin */}
        <div className="flex items-center gap-2 mb-7 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 themed-text-muted pointer-events-none" />
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={t('driveHome.projectsPage.filterPlaceholder')}
              data-testid="projects-filter"
              className="w-full themed-input ws-drive-pill rounded-lg pl-9 pr-3 py-2 text-sm"
            />
          </div>

          {/* Sort */}
          <div className="relative">
            <button onClick={() => { setSortOpen(o => !o); setFilterOpen(false) }} className="themed-btn-secondary rounded-lg px-3 py-2 text-sm flex items-center gap-1.5" data-testid="projects-sort">
              <ArrowUpDown size={14} />{SORT_LABEL[sort]}
            </button>
            {sortOpen && (
              <div className="absolute right-0 top-11 z-20 min-w-[170px] rounded-lg py-1 themed-bg-secondary themed-border border shadow-xl">
                {(['modified', 'created', 'name'] as ProjectSort[]).map(s => (
                  <button key={s} onClick={() => applySort(s)} data-active={sort === s || undefined}
                    className={`w-full text-left px-3 py-1.5 text-xs hover:themed-bg-tertiary transition-colors ${sort === s ? 'themed-text font-medium' : 'themed-text-muted'}`}>
                    {SORT_LABEL[s]}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Facet filter */}
          <div className="relative">
            <button onClick={() => { setFilterOpen(o => !o); setSortOpen(false) }} className="themed-btn-secondary rounded-lg px-3 py-2 text-sm flex items-center gap-1.5" data-testid="projects-facet">
              <Filter size={14} />
              {facet ? facet.label : t('driveHome.projectsPage.filterBy', { defaultValue: 'Filter' })}
              {facet && <X size={13} className="opacity-70 hover:opacity-100" onClick={(e) => { e.stopPropagation(); setFacet(null) }} />}
            </button>
            {filterOpen && (
              <div className="absolute right-0 top-11 z-20 min-w-[220px] max-h-[360px] overflow-y-auto rounded-lg py-1 themed-bg-secondary themed-border border shadow-xl">
                {FACET_GROUPS.every(g => facets[g.type].length === 0) ? (
                  <div className="px-3 py-2 text-xs themed-text-muted">{t('driveHome.projectsPage.noFilters', { defaultValue: 'No filters available' })}</div>
                ) : FACET_GROUPS.map(g => facets[g.type].length > 0 && (
                  <div key={g.type} className="py-1">
                    <div className="px-3 py-1 text-[10px] uppercase tracking-wide themed-text-muted">{g.label}</div>
                    {facets[g.type].map(f => (
                      <button key={`${f.type}:${f.value}`} onClick={() => { setFacet(f); setFilterOpen(false) }}
                        data-active={(facet?.type === f.type && facet?.value === f.value) || undefined}
                        className={`w-full text-left px-3 py-1.5 text-xs hover:themed-bg-tertiary transition-colors truncate ${facet?.type === f.type && facet?.value === f.value ? 'themed-text font-medium' : 'themed-text'}`}>
                        {f.label}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Show as admin — org admins/owners. Carved (inset) switch, same as the
              New Project dialog's Restricted toggle. */}
          {showAdminToggle && (
            <button onClick={() => setShowAsAdmin(v => !v)} role="switch" aria-checked={showAsAdmin} data-testid="projects-show-admin"
              className="flex items-center gap-2 shrink-0 themed-text-muted">
              <span className={`relative w-9 h-5 rounded-full transition-colors shrink-0 shadow-[inset_0_1px_3px_rgba(0,0,0,0.2)] ${showAsAdmin ? 'bg-violet-500' : 'themed-bg-primary'}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.35)] transition-all ${showAsAdmin ? 'left-[18px]' : 'left-0.5'}`} />
              </span>
              <span className="text-xs">{t('driveHome.projectsPage.showAsAdmin', { defaultValue: 'Show as admin' })}</span>
            </button>
          )}
        </div>

        {/* Body */}
        {projects.isLoading ? (
          <div className="text-sm themed-text-muted">{t('driveHome.loading')}</div>
        ) : visible.length === 0 ? (
          <div className="text-sm themed-text-muted py-8 text-center">
            {filter.trim() || facet ? t('driveHome.projectsPage.noMatches') : t('driveHome.projectsPage.empty')}
          </div>
        ) : !isOrg ? (
          grid(visible)
        ) : (
          <>
            {SECTION_ORDER.map(sec => {
              const items = sections[sec]
              if (items.length === 0) return null
              const isCollapsed = !!collapsed[sec]
              const meta = SECTION_META[sec]
              return (
                <section key={sec} className="mb-8">
                  <button
                    onClick={() => setCollapsed(c => ({ ...c, [sec]: !c[sec] }))}
                    className="ws-home-heading mb-3 flex items-center gap-1.5 hover:themed-text transition-colors"
                  >
                    {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                    {meta.icon}
                    {meta.label}
                    <span className="themed-text-muted font-normal">{items.length}</span>
                  </button>
                  {!isCollapsed && grid(items)}
                </section>
              )
            })}
          </>
        )}
        {/* #1484: this org's PDApps shared to me by a link that names me — Run only. */}
        {isMini && !projects.isLoading && <LinkAppSection filter={filter} enabled={deckActive} />}
      </div>

      {/* Share / access / rename / picker / details / debug dialogs. */}
      {dialogs}
    </div>
  )
}
